import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*

// Fáze 2: statické představení týmu + živý souhrn z bowlingligy (pozice v lize, poslední zápasy,
// příští zápas). Data z BL team-summary; v dev míří na local BL stack.

@Composable
fun HomeScreen() {
    var summary by remember { mutableStateOf<TeamSummaryDto?>(null) }
    var intro by remember { mutableStateOf<String?>(null) }
    var news by remember { mutableStateOf<List<SgNewsDto>>(emptyList()) }
    var error by remember { mutableStateOf<String?>(null) }
    var loading by remember { mutableStateOf(true) }

    LaunchedEffect(Unit) {
        try {
            intro = runCatching { Api.fetchContent("home_intro") }.getOrNull()
            news = runCatching { Api.fetchNews(null) }.getOrDefault(emptyList())
            summary = Api.fetchTeamSummary()
        } catch (e: Throwable) {
            error = e.apiMessage()
        } finally {
            loading = false
        }
    }

    Div(attrs = { classes("home-grid") }) {
        Div(attrs = { classes("card", "home-intro") }) {
            H2 { Text("O týmu") }
            val text = intro?.takeIf { it.isNotBlank() }
            if (text != null) RichContent(text)
            else P(attrs = { classes("intro-text") }) { Text("Sunny Gang Bowling je bowlingový tým hrající 3. ligu.") }
        }

        if (news.isNotEmpty()) {
            Div(attrs = { classes("card", "home-news") }) {
                H3 { Text("Aktuality") }
                Div(attrs = { classes("news-list") }) {
                    news.forEach { NewsItemCard(it) }
                }
            }
        }

        when {
            loading -> Div(attrs = { classes("card", "home-message") }) { P { Text("Načítám…") } }
            summary != null -> {
                summary!!.standing?.let { StandingCard(it) }
                NextMatchCard(summary!!.nextMatch, summary!!.overdueMatches)
                LastMatchesCard(summary!!.lastMatches)
            }
            else -> Div(attrs = { classes("card", "home-message") }) {
                P { Text("Živá ligová data se zobrazí po nasazení bowlingliga team API (fáze 1).") }
                error?.let { Div(attrs = { classes("muted") }) { Text("Detail: $it") } }
            }
        }
    }
}

@Composable
private fun NewsItemCard(n: SgNewsDto) {
    Div(attrs = { classes("news-item") }) {
        Div(attrs = { classes("news-head") }) {
            Span(attrs = { classes("news-title") }) { Text(n.title) }
            n.createdAt?.take(10)?.let { Span(attrs = { classes("news-date") }) { Text(it) } }
        }
        if (n.body.isNotBlank()) Div(attrs = { classes("news-body") }) { RichContent(n.body) }
    }
}

@Composable
private fun StandingCard(s: TeamStandingDto) {
    Div(attrs = { classes("card", "standing-card", "home-standing") }) {
        Div(attrs = { classes("standing-rank") }) {
            Div(attrs = { classes("rank-number") }) { Text("${s.position ?: "–"}.") }
            Div(attrs = { classes("rank-label") }) { Text("místo") }
        }
        Div(attrs = { classes("standing-info") }) {
            Div(attrs = { classes("standing-league") }) { Text(s.leagueName) }
            A(
                href = "https://bowlingliga.eu/2liga",
                attrs = {
                    classes("standing-link")
                    attr("target", "_blank")
                    attr("rel", "noopener noreferrer")
                }
            ) { Text("Kompletní pořadí ligy ↗") }
            Div(attrs = { classes("standing-stats") }) {
                StatPill("${s.points}", "bodů")
                StatPill("${s.played}", "zápasů")
            }
        }
    }
}

@Composable
private fun StatPill(value: String, label: String) {
    Div(attrs = { classes("stat-pill") }) {
        Span(attrs = { classes("stat-value") }) { Text(value) }
        Span(attrs = { classes("stat-label") }) { Text(label) }
    }
}

@Composable
internal fun NextMatchCard(next: TeamMatchDto?, overdue: List<TeamMatchDto> = emptyList()) {
    if (next == null && overdue.isEmpty()) return
    Div(attrs = { classes("home-next", "match-schedule") }) {
        if (next != null) Div(attrs = { classes("card") }) {
            H3 { Text("Příští zápas") }
            Div(attrs = { classes("next-match") }) {
                Span(attrs = { classes("match-meta") }) { Text(roundLabel(next)) }
                Span(attrs = { classes("next-date") }) { Text(matchWindowLabel(next)) }
                Span { Text(if (next.home) "Domácí vs. " else "Hosté vs. ") }
                Span(attrs = { classes("next-opponent") }) { Text(next.opponent) }
            }
        }
        if (overdue.isNotEmpty()) Div(attrs = { classes("card", "overdue-matches") }) {
            H3 { Text("Neodehrané zápasy po termínu") }
            P(attrs = { classes("muted") }) { Text("Termín kola uplynul, ale zápas je stále veden jako neodehraný.") }
            overdue.forEach { match ->
                Div(attrs = { classes("overdue-match") }) {
                    Div(attrs = { classes("next-opponent") }) { Text("${if (match.home) "Domácí" else "Hosté"} vs. ${match.opponent}") }
                    Div(attrs = { classes("match-meta") }) { Text(roundLabel(match)) }
                    Div(attrs = { classes("overdue-date") }) { Text(matchWindowLabel(match)) }
                }
            }
        }
    }
}

@Composable
private fun LastMatchesCard(matches: List<TeamMatchDto>) {
    Div(attrs = { classes("card", "home-matches") }) {
        H3 { Text("Poslední zápasy") }
        if (matches.isEmpty()) {
            P(attrs = { classes("muted") }) { Text("Zatím žádné odehrané zápasy.") }
            return@Div
        }
        Div(attrs = { classes("match-list") }) {
            matches.forEach { MatchRow(it) }
        }
    }
}

@Composable
private fun MatchRow(m: TeamMatchDto) {
    Div(attrs = { classes("match-row") }) {
        // Vizuální výsledek: barevný odznak V/P/R
        Div(attrs = { classes("result-badge", resultClass(m.result)) }) { Text(resultShort(m.result)) }

        Div(attrs = { classes("match-main") }) {
            Div(attrs = { classes("match-opponent") }) {
                Text(m.opponent)
            }
            Div(attrs = { classes("match-meta") }) {
                Text(roundLabel(m))
                m.date?.let { Span(attrs = { classes("dot") }) { Text(" · ") }; Text(it) }
            }
        }

        Div(attrs = { classes("match-scores") }) {
            // Duelové skóre (rozhoduje výsledek)
            Div(attrs = { classes("duel-score") }) {
                Text("${m.teamDuelPoints ?: "–"} : ${m.oppDuelPoints ?: "–"}")
            }
            // Celkový nához (doplňkově)
            Div(attrs = { classes("pinfall-score") }) {
                Text("nához ${m.teamScore ?: "–"} : ${m.oppScore ?: "–"}")
            }
        }
    }
}

private fun resultClass(result: String?): String = when (result) {
    "win" -> "result-win"
    "loss" -> "result-loss"
    "draw" -> "result-draw"
    else -> "result-none"
}

private fun resultShort(result: String?): String = when (result) {
    "win" -> "V"
    "loss" -> "P"
    "draw" -> "R"
    else -> "?"
}

private fun phaseLabel(phase: String?): String = when (phase) {
    "autumn" -> "podzim"
    "spring" -> "jaro"
    "playoff_title" -> "nadstavba o titul"
    "playoff_relegation" -> "nadstavba o udržení"
    else -> phase ?: ""
}

private fun roundLabel(m: TeamMatchDto): String {
    val round = m.roundNumber?.let { "$it. kolo" } ?: ""
    val phase = phaseLabel(m.phase)
    return listOf(round, phase).filter { it.isNotEmpty() }.joinToString(" · ")
}
